import React, { useEffect } from "react";
import {
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Paper,
} from "@mui/material";
import { usePumpageSummary } from "./usePumpageSummary";

export default function PumpageSummary({ permitNdx, year, onRefetch }) {
  const { data, isLoading, isError, refetch } = usePumpageSummary(
    permitNdx,
    year
  );

  // Expose refetch function to parent component
  useEffect(() => {
    if (onRefetch) {
      onRefetch(refetch);
    }
  }, [onRefetch, refetch]);

  if (!permitNdx || !year) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Select a Permit Holder and Year to view the Pumpage Summary.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" my={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load pumpage summary data.
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body1" fontWeight="bold">
          No summary data available
        </Typography>
        <Typography variant="body2">
          <strong>Permit:</strong> {permitNdx ?? "N/A"}
        </Typography>
        <Typography variant="body2">
          <strong>Year:</strong> {year ?? "N/A"}
        </Typography>
      </Alert>
    );
  }

  return (
    <>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {year} Annual Pumpage Summary
      </Typography>
      <Grid container spacing={3}>
        {/* Allocation Section */}
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Permit Annual Allocation
            </Typography>

            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" fontWeight="bold">
                Acre Feet:
              </Typography>
              <Typography variant="body1">
                {data.allocation_acre_feet ?? "N/A"}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="body2" fontWeight="bold">
                Gallons / Year:
              </Typography>
              <Typography variant="body1">
                {data.allocation_gpy ?? "N/A"}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                border: "1px solid",
                borderColor: "secondary.main",
                borderRadius: 1,
                p: 2,
                backgroundColor: "rgba(0, 0, 0, 0.03)",
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {year} Adjusted Allocation
              </Typography>

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" fontWeight="bold">
                  Acre Feet:
                </Typography>
                <Typography variant="body1">
                  {data.adjusted_acft ?? "N/A"}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" fontWeight="bold">
                  Gallons / Year:
                </Typography>
                <Typography variant="body1">
                  {data.adjusted_gpy ?? "N/A"}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Pumpage Section */}
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Pumpage Overview
            </Typography>

            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" fontWeight="bold">
                Q1 Pumped:
              </Typography>
              <Typography variant="body1">{data.q1_pumped ?? "0"}</Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" fontWeight="bold">
                Q2 Pumped:
              </Typography>
              <Typography variant="body1">{data.q2_pumped ?? "0"}</Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" fontWeight="bold">
                Q3 Pumped:
              </Typography>
              <Typography variant="body1">{data.q3_pumped ?? "0"}</Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="body2" fontWeight="bold">
                Q4 Pumped:
              </Typography>
              <Typography variant="body1">{data.q4_pumped ?? "0"}</Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" fontWeight="bold">
                YTD Pumped:
              </Typography>
              <Typography variant="body1">{data.ytd_pumped ?? "0"}</Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" fontWeight="bold">
                % Pumped (YTD):
              </Typography>
              <Typography variant="body1">
                {data.percent_pumped_ytd ? `${data.percent_pumped_ytd}%` : "0%"}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Loss Section */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Most Recent Meter Reading
            </Typography>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center">
                <Typography variant="body2" fontWeight="bold" sx={{ mr: 1 }}>
                  Meter Reading:
                </Typography>
                <Typography variant="body1">
                  {data.meter_reading ?? "N/A"}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center">
                <Typography variant="body2" fontWeight="bold" sx={{ mr: 1 }}>
                  Reading Date:
                </Typography>
                <Typography variant="body1">
                  {typeof data.meter_reading_rpt_month === "number" &&
                  typeof data.meter_reading_rpt_year === "number"
                    ? `${new Date(
                        0,
                        data.meter_reading_rpt_month - 1
                      ).toLocaleString("en-US", { month: "short" })} ${
                        data.meter_reading_rpt_year
                      }`
                    : "N/A"}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Most Recent Pumpage Section */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Most Recent Pumpage
            </Typography>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center">
                <Typography variant="body2" fontWeight="bold" sx={{ mr: 1 }}>
                  Pumpage Amount:
                </Typography>
                <Typography variant="body1">
                  {data.latest_pumpage ?? "N/A"}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center">
                <Typography variant="body2" fontWeight="bold" sx={{ mr: 1 }}>
                  Pumpage Date:
                </Typography>
                <Typography variant="body1">
                  {typeof data.latest_pumpage_rpt_month === "number" &&
                  typeof data.latest_pumpage_rpt_year === "number"
                    ? `${new Date(
                        0,
                        data.latest_pumpage_rpt_month - 1
                      ).toLocaleString("en-US", { month: "short" })} ${
                        data.latest_pumpage_rpt_year
                      }`
                    : "N/A"}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}
